import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { UiBudgetLane, UiBudgets } from '@nightshift/ui-types';

import { BudgetLanePanel } from '../components/budget-lane.tsx';
import { Empty, Kicker, Spinner } from '../components/primitives.tsx';
import { api, ApiError } from '../live/api.ts';
import { useLive, useStore } from '../live/context.tsx';
import {
  defaultLaneSelection,
  parseLaneSelection,
  plottableLanes,
  pointsForPick,
  serializeLaneSelection,
  stepLanePick,
  type LanePick,
} from '../lib/budget-lanes.ts';
import {
  emptyLaneReason,
  formatLatest,
  formatMetricValue,
  formatTargetLine,
  formatTargetSentence,
  LANE_STATUS_LABEL,
  shortSha,
} from '../lib/budgets.ts';
import { absoluteTime, relativeShort } from '../lib/format.ts';
import { shortcutBlocked } from '../lib/keys.ts';
import { retryPhrase } from '../lib/liveness.ts';
import type { ViewProps } from './board.tsx';
import './budgets.css';

export interface BudgetsViewProps extends ViewProps {
  selectedId: string | null;
  onSelectId: (id: string | null) => void;
  selectedProbe: string | null;
  onSelectProbe: (probe: string | null) => void;
}

const LANE_STORE_KEY = 'nightshift-budget-lanes-v2';

/**
 * Performance bench: one lane per canonical metric from `performance_samples`
 * and Settings targets. Nightly is a line, MR is scatter, the target is a rule.
 * This screen does not fail CI.
 */
export function BudgetsView({
  selectedId,
  onSelectId,
  selectedProbe,
  onSelectProbe,
}: BudgetsViewProps) {
  const store = useStore();
  const state = useLive();
  const snapshot = state.snapshot;
  const [page, setPage] = useState<UiBudgets | null>(null);
  const [indexError, setIndexError] = useState<string | null>(null);
  const [indexPending, setIndexPending] = useState(true);
  const [chosen, setChosen] = useState<string[] | null>(null);
  const [picked, setPicked] = useState<LanePick | null>(null);
  const [range, setRange] = useState<{ range?: string; from?: number; to?: number }>({ range: 'auto' });
  const [rangeName, setRangeName] = useState('auto');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [rangeError, setRangeError] = useState('');
  const loadedWindow = useRef('');

  const projectSlug = snapshot?.projectSlug ?? null;
  const revision = snapshot?.revision;

  useEffect(() => {
    if (revision === undefined) return;
    let alive = true;
    setIndexPending(true);
    void api
      .budgets({ project: projectSlug, ...range })
      .then((body) => {
        if (!alive) return;
        setPage(body);
        setPicked(null);
        const dateStart = new Date(body.windowFrom).toISOString().slice(0, 10);
        const dateEnd = new Date(body.windowTo).toISOString().slice(0, 10);
        const key = `${projectSlug}:${dateStart}:${dateEnd}`;
        if (loadedWindow.current !== key) {
          loadedWindow.current = key;
          setStart(dateStart);
          setEnd(dateEnd);
        }
        setIndexError(null);
      })
      .catch((caught: unknown) => {
        if (!alive) return;
        if (caught instanceof ApiError && caught.status === 401) {
          setIndexError(caught.message);
          return;
        }
        setIndexError(caught instanceof Error ? caught.message : String(caught));
      })
      .finally(() => {
        if (alive) setIndexPending(false);
      });
    return () => {
      alive = false;
    };
  }, [projectSlug, revision, range]);

  const lanes = useMemo(() => plottableLanes(page?.lanes ?? []), [page]);

  useEffect(() => {
    if (chosen !== null || lanes.length === 0) return;
    let stored: string | null;
    try {
      stored = window.localStorage.getItem(LANE_STORE_KEY);
    } catch {
      stored = null;
    }
    setChosen(parseLaneSelection(stored, lanes) ?? defaultLaneSelection(lanes));
  }, [chosen, lanes]);

  const toggleLane = useCallback(
    (id: string) => {
      setChosen((current) => {
        const base = current ?? defaultLaneSelection(lanes);
        const next = base.includes(id) ? base.filter((entry) => entry !== id) : [...base, id];
        try {
          window.localStorage.setItem(LANE_STORE_KEY, serializeLaneSelection(next));
        } catch {
          // Private mode: the choice still applies for this visit.
        }
        return next;
      });
      setPicked((current) => (current?.laneId === id ? null : current));
      if (selectedId === id) {
        onSelectId(null);
        onSelectProbe(null);
      }
    },
    [lanes, onSelectId, onSelectProbe, selectedId],
  );

  const linked = useRef<string | null>(null);
  useEffect(() => {
    if (!selectedId || chosen === null) return;
    const lane = lanes.find((entry) => entry.id === selectedId);
    if (!lane) return;
    if (!chosen.includes(lane.id)) {
      setChosen((current) => [...(current ?? []), lane.id]);
      return;
    }
    const key = `${selectedId}:${selectedProbe ?? ''}`;
    if (linked.current === key) return;
    linked.current = key;
    const id = `budget-lane-${lane.id}`;
    let cancelled = false;
    const scroll = () => {
      if (cancelled) return;
      document.getElementById(id)?.scrollIntoView({ block: 'start', behavior: 'smooth' });
    };
    const timer = window.setTimeout(scroll, 80);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [chosen, lanes, selectedId, selectedProbe]);

  const chosenSet = useMemo(() => new Set(chosen ?? []), [chosen]);
  const activeLanes = useMemo(
    () => lanes.filter((lane) => chosenSet.has(lane.id)),
    [chosenSet, lanes],
  );
  const pickedLane = picked
    ? (activeLanes.find((entry) => entry.id === picked.laneId)?.lane ?? null)
    : null;

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (shortcutBlocked(event)) return;
      if (event.key === 'Escape' && picked) {
        setPicked(null);
        return;
      }
      if (!picked || !pickedLane) return;
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        event.preventDefault();
        setPicked(stepLanePick(pickedLane, picked, event.key === 'ArrowLeft' ? -1 : 1));
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [picked, pickedLane]);

  if (state.loadError && state.loadError !== 'session-expired' && !snapshot) {
    return (
      <Empty
        title="The budgets hub could not be read."
        body={state.loadError}
        action={
          <button type="button" className="btn" onClick={() => void store.refetch()}>
            Try again
          </button>
        }
      />
    );
  }

  if (!snapshot) {
    return (
      <div className="budgets-state" aria-label="Loading budgets">
        <Spinner size={20} />
        <span>Reading performance samples…</span>
      </div>
    );
  }

  const stale = state.connection !== 'live';
  const items = page?.lanes ?? [];
  const regressions = items.filter((item) => item.regression).length;
  const emptyLanes = items.filter((item) => item.status === 'empty').length;

  return (
    <div className="budgets">
      <header className="budgets-bar">
        <div>
          <h1>Performance</h1>
          <p>
            Compare probes, spot unusual runs and track performance against your targets.
          </p>
        </div>
        <div className="budgets-bar-facts">
          {regressions > 0 ? <Kicker count={regressions}>Regressions</Kicker> : null}
          {emptyLanes > 0 ? <Kicker count={emptyLanes}>Unmeasured</Kicker> : null}
          {stale ? (
            <span className="budgets-stale" role="status">
              Showing last confirmed {relativeShort(state.lastContactAt, state.tick)} ago
              {retryPhrase(state.reconnectInSeconds)}
            </span>
          ) : (
            <span className="budgets-live">live</span>
          )}
        </div>
      </header>

      <form className="budget-range" onSubmit={(event) => {
        event.preventDefault();
        const from = Date.parse(`${start}T00:00:00Z`);
        const to = Date.parse(`${end}T00:00:00Z`) + 86_400_000 - 1;
        if (!Number.isFinite(from) || !Number.isFinite(to) || from >= to) {
          setRangeError('Choose a start date on or before the end date.');
          return;
        }
        setRangeError('');
        setRangeName('custom');
        setRange({ from, to });
      }}>
        <div className="budget-presets" role="group" aria-label="Date range">
          {['auto', '7', '30', '60', '90'].map((value) => (
            <button type="button" className="btn" key={value} aria-pressed={rangeName === value}
              onClick={() => {
                setRangeName(value);
                setRangeError('');
                setRange(value === 'auto' ? { range: 'auto' } : { from: Date.now() - Number(value) * 86_400_000, to: Date.now() });
              }}>{value === 'auto' ? 'Auto' : `${value} days`}</button>
          ))}
        </div>
        <label>From <input type="date" aria-label="From date" value={start} onChange={(event) => setStart(event.target.value)} required /></label>
        <label>To <input type="date" aria-label="To date" value={end} onChange={(event) => setEnd(event.target.value)} required /></label>
        <button className="btn" type="submit">Apply range</button>
        <span className="budget-range-caption" role="status">{rangeError || (indexPending ? 'Updating…' : rangeName === 'auto' ? 'Fits recorded data · up to 60 days · UTC' : rangeName === 'custom' ? 'Custom dates include the whole day · UTC' : `Last ${rangeName} days · UTC`)}</span>
      </form>

      {indexError ? (
        <Empty
          title="Budgets could not be read."
          body={indexError}
          action={
            <button type="button" className="btn" onClick={() => void store.refetch()}>
              Try again
            </button>
          }
        />
      ) : indexPending && !page ? (
        <div className="budgets-state" aria-label="Loading budgets">
          <Spinner size={20} />
          <span>Assembling samples and targets…</span>
        </div>
      ) : items.length === 0 ? (
        <Empty
          title="Nothing measured yet."
          body="No performance samples or targets in this range. Choose Auto to find recorded data, or widen the dates. Targets are edited on Settings → Performance."
        />
      ) : (
        <>
          <div className="bench-chips" role="group" aria-label="Metrics on the bench">
            {lanes.map((entry) => (
              <button
                key={entry.id}
                type="button"
                className="bench-chip"
                aria-pressed={chosenSet.has(entry.id)}
                onClick={() => toggleLane(entry.id)}
              >
                <span className="lane-swatch" style={{ background: entry.hue }} aria-hidden="true" />
                {entry.lane.label}
                {entry.lane.regression ? <span className="bench-chip-lamp" aria-hidden="true" /> : null}
              </button>
            ))}
          </div>

          {items.some((item) => item.metric === 'fps_avg') && !items.some((item) => item.metric === 'fps_p95') ? (
            <p className="budget-missing">P95 FPS has not been reported in this range. Include <code>fps_p95</code> in the performance probe output to chart it alongside average FPS.</p>
          ) : null}

          {activeLanes.length === 0 ? (
            <Empty
              title="Every chart is switched off."
              body="Turn a metric back on above to see its measurements."
            />
          ) : (
            <div className="bench" aria-label="Budget lanes">
              {activeLanes.map((entry) => (
                <BudgetLanePanel
                  key={entry.id}
                  lane={entry.lane}
                  hue={entry.hue}
                  windowFrom={page?.windowFrom ?? 0}
                  windowTo={page?.windowTo ?? 0}
                  now={state.tick}
                  probe={selectedId === entry.id ? selectedProbe : null}
                  picked={picked}
                  onPick={setPicked}
                  onSelectProbe={(probe) => {
                    onSelectId(entry.id);
                    onSelectProbe(probe);
                  }}
                />
              ))}
              <p className="bench-note">
                Circles and solid lines: nightly. Squares and dotted lines: merge requests.
                Amber: regression or statistical outlier. Dashed horizontal line: target.
                Select a point for its machine, commit and run details. Targets: Settings → Performance. These charts do not fail CI.
              </p>
            </div>
          )}

          {picked && pickedLane ? (
            <PickedStrip
              lane={pickedLane}
              pick={picked}
              onClear={() => setPicked(null)}
            />
          ) : null}

          <ProbeLedger items={items} onOpen={(id, probe) => {
            onSelectId(id);
            onSelectProbe(probe);
          }} />
        </>
      )}
    </div>
  );
}

function PickedStrip({
  lane,
  pick,
  onClear,
}: {
  lane: UiBudgetLane;
  pick: LanePick;
  onClear: () => void;
}) {
  const obs = pointsForPick(lane, pick)[pick.index];
  if (!obs) return null;
  return (
    <div className="bench-picked" role="status" aria-label="Selected sample">
      <span className={`bench-picked-kind${obs.regression ? ' is-regression' : ''}`}>
        {obs.context}
      </span>
      <span className="bench-picked-value">{formatMetricValue(lane.unit, obs.value)}</span>
      {obs.regression ? <span className="bench-picked-flag">regression</span> : null}
      {obs.noBaseline ? <span className="bench-picked-flag is-quiet">no baseline</span> : null}
      <span className="bench-picked-source">
        {obs.probe ?? 'run'} · {obs.machine}
        <span className="mono"> {shortSha(obs.sha)}</span>
        {' · '}
        {absoluteTime(obs.t)}
      </span>
      <span className="bench-picked-target">{formatTargetSentence(lane)}</span>
      <span className="bench-picked-actions">
        <button type="button" className="btn btn-ghost btn-sm" onClick={onClear}>
          Clear
        </button>
      </span>
    </div>
  );
}

function ProbeLedger({
  items,
  onOpen,
}: {
  items: readonly UiBudgetLane[];
  onOpen: (metric: string, probe: string | null) => void;
}) {
  return (
    <div className="ledger-wrap">
      <table className="ledger" aria-label="Every metric and probe">
        <thead>
          <tr>
            <th scope="col">Status</th>
            <th scope="col">Metric</th>
            <th scope="col">Probe</th>
            <th scope="col" className="ledger-num">
              Latest
            </th>
            <th scope="col" className="ledger-num">
              Target
            </th>
            <th scope="col" className="ledger-num">
              Nightly
            </th>
            <th scope="col" className="ledger-num">
              MR
            </th>
            <th scope="col">
              <span className="sr-only">Open</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => {
            if (item.probes.length === 0) {
              return (
                <tr key={item.id} title={emptyLaneReason(item)}>
                  <td>
                    <span className={`budgets-status is-${item.status}`}>
                      {LANE_STATUS_LABEL[item.status]}
                    </span>
                  </td>
                  <td className="ledger-budget">
                    <span className="ledger-budget-label">{item.label}</span>
                    <span className="ledger-budget-kind">{item.metric}</span>
                  </td>
                  <td className="dim">—</td>
                  <td className="ledger-num mono">{formatLatest(item)}</td>
                  <td className="ledger-num mono">{formatTargetLine(item)}</td>
                  <td className="ledger-num num">0</td>
                  <td className="ledger-num num">0</td>
                  <td className="ledger-actions">
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={() => onOpen(item.id, null)}
                    >
                      Open
                    </button>
                  </td>
                </tr>
              );
            }
            return item.probes.map((probe) => (
              <tr key={`${item.id}:${probe.name}`}>
                <td>
                  <span
                    className={`budgets-status is-${probe.regression ? 'regression' : probe.noBaseline ? 'no-baseline' : item.status === 'empty' ? 'empty' : 'ok'}`}
                  >
                    {probe.regression
                      ? 'regression'
                      : probe.noBaseline
                        ? 'no baseline'
                        : LANE_STATUS_LABEL[item.status === 'regression' ? 'ok' : item.status]}
                  </span>
                </td>
                <td className="ledger-budget">
                  <span className="ledger-budget-label">{item.label}</span>
                  <span className="ledger-budget-kind">{item.metric}</span>
                </td>
                <td>{probe.name}</td>
                <td className="ledger-num mono">
                  {formatLatest({
                    ...item,
                    nightly: probe.nightly,
                    mr: probe.mr,
                  })}
                </td>
                <td className="ledger-num mono">{formatTargetLine(item)}</td>
                <td className="ledger-num num">{probe.nightly.length}</td>
                <td className="ledger-num num">{probe.mr.length}</td>
                <td className="ledger-actions">
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={() => onOpen(item.id, probe.name)}
                  >
                    Open
                  </button>
                </td>
              </tr>
            ));
          })}
        </tbody>
      </table>
    </div>
  );
}
